/**
 * VenueFlow Dropbox Service (proxied, parallel)
 *
 * Uploads go through our Vercel function (/api/dropbox-upload), and chunks are
 * sent SEVERAL AT A TIME instead of one-by-one. This removes the "stop / jerk /
 * stop" slowness of serial uploads, where the connection sat idle between each
 * chunk's round trip.
 *
 * HOW PARALLEL IS SAFE:
 * A normal Dropbox upload session requires chunks in strict offset order, which
 * is why serial was the only option before. We instead start a CONCURRENT
 * session (?op=start&concurrent=1), which lets chunks arrive out of order. The
 * one rule for concurrent sessions: every chunk except the final tail must be an
 * exact multiple of 4MB. CHUNK_SIZE is exactly 4MB, and we upload the sub-4MB
 * tail (if any) last, so that rule always holds.
 *
 * Exported uploadToDropbox(file, path, onProgress, abortController) signature is
 * unchanged - no component edits needed.
 *
 * REQUIRES the updated api/dropbox-upload.js (which adds concurrent-session
 * support to the start op). Deploy both together.
 */

const PROXY_ENDPOINT = "/api/dropbox-upload";

// Must be EXACTLY 4MB: it stays under Vercel's ~4.5MB request-body cap AND it is
// the 4194304-byte multiple that Dropbox concurrent sessions require per chunk.
const CHUNK_SIZE = 4 * 1024 * 1024; // 4194304 bytes

// How many chunks to upload at once. 4 fills the pipe without hammering Dropbox
// into rate limits (429s). Tune cautiously.
const CONCURRENCY = 4;

const MAX_RETRIES = 4;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ------------------------------------------------------------------
// Proxy call helper
// ------------------------------------------------------------------
async function proxyCall(op, { body, signal, query } = {}) {
  const qs = new URLSearchParams({ op, ...(query || {}) }).toString();
  const res = await fetch(`${PROXY_ENDPOINT}?${qs}`, {
    method: "POST",
    signal,
    ...(body !== undefined ? { body } : {}),
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = text;
  }

  if (!res.ok) {
    const detail =
      data && data.detail
        ? typeof data.detail === "string"
          ? data.detail
          : JSON.stringify(data.detail)
        : data && data.error
        ? data.error
        : "";
    const err = new Error(`Dropbox ${op} failed (${res.status})${detail ? `: ${detail}` : ""}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// ------------------------------------------------------------------
// Chunked upload (concurrent session + bounded parallelism)
// ------------------------------------------------------------------
async function uploadChunked(file, path, onProgress, abortController) {
  const signal = abortController ? abortController.signal : undefined;
  const total = file.size;

  // 1. Start a CONCURRENT session
  const { session_id: sessionId } = await proxyCall("start", {
    signal,
    query: { concurrent: "1" },
  });
  if (!sessionId) throw new Error("Dropbox start returned no session_id");

  // Build all chunks (every full chunk is exactly 4MB; the very last may be
  // shorter). We set the FINAL chunk aside as the "closer": it is the only chunk
  // sent with close:true, and the only one allowed to be a non-4MB size. Every
  // other chunk is a full 4MB block, which is what concurrent sessions require.
  const chunks = [];
  for (let offset = 0; offset < total; offset += CHUNK_SIZE) {
    chunks.push({ offset, end: Math.min(offset + CHUNK_SIZE, total) });
  }
  const closer = chunks.length > 0 ? chunks.pop() : null; // final chunk

  let uploadedBytes = 0;
  const reportProgress = () => {
    if (onProgress && total > 0) {
      onProgress(Math.min(100, Math.round((uploadedBytes / total) * 100)));
    }
  };

  async function uploadChunk({ offset, end }, close = false) {
    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (signal && signal.aborted) throw new DOMException("Aborted", "AbortError");
      try {
        const chunk = file.slice(offset, end);
        await proxyCall("append", {
          body: chunk,
          signal,
          query: {
            session_id: sessionId,
            offset: String(offset),
            ...(close ? { close: "1" } : {}),
          },
        });
        uploadedBytes += end - offset;
        reportProgress();
        return;
      } catch (err) {
        if (signal && signal.aborted) throw err; // cancelled - don't retry
        attempt += 1;
        if (attempt > MAX_RETRIES) throw err;
        await sleep(500 * attempt); // backoff on transient failures
      }
    }
  }

  // 2. Upload all non-final chunks in parallel (all full 4MB, close:false)
  let next = 0;
  async function worker() {
    while (next < chunks.length) {
      const idx = next++;
      await uploadChunk(chunks[idx]);
    }
  }
  const poolSize = Math.min(CONCURRENCY, chunks.length);
  await Promise.all(Array.from({ length: poolSize }, () => worker()));

  // 3. Send the final chunk alone with close:true. This is the one chunk allowed
  //    to be shorter than 4MB, and it ends the concurrent session.
  if (closer) await uploadChunk(closer, true);

  // 4. Finish (commit)
  const finishData = await proxyCall("finish", {
    signal,
    body: JSON.stringify({ session_id: sessionId, offset: total, path }),
  });

  // 5. Temporary link (use Dropbox's returned path in case of autorename)
  const linkData = await proxyCall("temp_link", {
    signal,
    body: JSON.stringify({ path: finishData.path_lower || path }),
  });

  return linkData.link;
}

// ------------------------------------------------------------------
// Public API (unchanged signature)
// ------------------------------------------------------------------
export async function uploadToDropbox(file, path, onProgress, abortController) {
  console.log("🔥 Dropbox upload start (via proxy, parallel):", { path, file: file.name });
  const url = await uploadChunked(file, path, onProgress, abortController);
  console.log("✅ Dropbox upload finished:", url);
  return url;
}

// ------------------------------------------------------------------
// Backwards-compatible exports (nothing currently uses these, kept for safety)
// ------------------------------------------------------------------
export const dropboxUploader = {
  upload: (file, path, onProgress, abortController) =>
    uploadToDropbox(file, path, onProgress, abortController),
};

const dropboxService = { uploadToDropbox, dropboxUploader };
export default dropboxService;
